/**
 * Leave request form — extracted from my_leave_screen.tsx per the >150-line
 * component rule. Owns the form state; calls `submit` on success and surfaces
 * server-side error messages inline (never a raw toast).
 */
import { useState } from 'react';
import { useTranslation } from '@mukil/i18n';
import { Button, DateInput, StyledSelect, Textarea } from '@jumpstone/ui-react';
import type {
  MyLeaveSubmitInput,
  MyLeaveActionResult,
} from '../../data/use_my_leave.js';
import type {
  MyLeaveBalance,
  MyLeaveType,
  LeaveRequestFormError,
} from './my_leave_utils.js';
import {
  find_balance_for_type,
  validate_leave_request_form,
  derive_days_requested_scaled,
  leave_request_form_error_message,
} from './my_leave_utils.js';
import { MyLeaveBalanceSummary } from './my_leave_balance_summary.js';

/** Non-nullable alias for use as a parameter type in `error_for`. */
type LeaveRequestFormErrorKey = LeaveRequestFormError;

interface LeaveRequestFormProps {
  leave_types: MyLeaveType[];
  /** Balances for the selected type's inline summary (refreshed after submit). */
  balances: MyLeaveBalance[];
  submit: (input: MyLeaveSubmitInput) => Promise<MyLeaveActionResult>;
  on_success: () => void;
}

export function LeaveRequestForm({
  leave_types,
  balances,
  submit,
  on_success,
}: LeaveRequestFormProps): React.JSX.Element {
  const { t } = useTranslation('wp');

  const [leave_type, set_leave_type] = useState('');
  const [start_date, set_start_date] = useState('');
  const [end_date, set_end_date] = useState('');
  const [notes, set_notes] = useState('');
  const [is_submitting, set_is_submitting] = useState(false);
  const [touched, set_touched] = useState(false);
  const [server_error, set_server_error] = useState<string | null>(null);

  const selected_balance = find_balance_for_type(balances, leave_type);

  const validation_error = validate_leave_request_form({
    leave_type,
    start_date,
    end_date,
    notes,
  });

  function error_for(key: LeaveRequestFormErrorKey): string | null {
    if (!touched) return null;
    return validation_error === key
      ? leave_request_form_error_message(t, key)
      : null;
  }

  const handle_submit = (): void => {
    set_touched(true);
    if (validation_error) return;

    const days_requested = derive_days_requested_scaled(start_date, end_date);
    if (days_requested === null) return;

    set_is_submitting(true);
    set_server_error(null);

    void submit({
      leave_type,
      start_date,
      end_date,
      days_requested,
      notes: notes.trim() || undefined,
    })
      .then((result) => {
        if (result.outcome === 'success') {
          // Reset form.
          set_leave_type('');
          set_start_date('');
          set_end_date('');
          set_notes('');
          set_touched(false);
          on_success();
        } else {
          // Surface the server error inline.
          switch (result.error) {
            case 'overlap':
              set_server_error(t('my_leave.form.error.overlap'));
              break;
            case 'invalid_type':
              set_server_error(t('my_leave.form.error.invalid_type'));
              break;
            case 'unknown':
              set_server_error(t('my_leave.form.error.unknown'));
              break;
          }
        }
      })
      .finally(() => {
        set_is_submitting(false);
      });
  };

  return (
    <div className="flex flex-col gap-4">
      {/* Leave type */}
      <div>
        <label className="nv-label">
          {t('my_leave.form.leave_type_label')}
        </label>
        <StyledSelect
          value={leave_type}
          onChange={(v: string) => {
            set_leave_type(v);
          }}
          ariaLabel={t('my_leave.form.leave_type_label')}
          placeholder={t('my_leave.form.leave_type_placeholder')}
          disabled={is_submitting}
          options={leave_types.map((lt) => ({
            value: lt.code,
            label: lt.label,
          }))}
        />
        {error_for('leave_type_required') && (
          <p className="nv-help is-error mt-1" role="alert">
            {error_for('leave_type_required')}
          </p>
        )}
        {selected_balance !== null && (
          <div className="mt-2">
            <MyLeaveBalanceSummary balance={selected_balance} compact />
          </div>
        )}
      </div>

      {/* Start / End dates — row on md+, stacked on mobile */}
      <div className="flex flex-col gap-4 sm:flex-row sm:gap-3">
        <div className="flex-1">
          <label className="nv-label" htmlFor="my-leave-start">
            {t('my_leave.form.start_date_label')}
          </label>
          <DateInput
            id="my-leave-start"
            value={start_date}
            onChange={(e) => {
              set_start_date(e.target.value);
            }}
            disabled={is_submitting}
          />
          {error_for('start_date_required') && (
            <p className="nv-help is-error mt-1" role="alert">
              {error_for('start_date_required')}
            </p>
          )}
        </div>
        <div className="flex-1">
          <label className="nv-label" htmlFor="my-leave-end">
            {t('my_leave.form.end_date_label')}
          </label>
          <DateInput
            id="my-leave-end"
            value={end_date}
            onChange={(e) => {
              set_end_date(e.target.value);
            }}
            disabled={is_submitting}
          />
          {(error_for('end_date_required') ??
            error_for('end_before_start')) && (
            <p className="nv-help is-error mt-1" role="alert">
              {error_for('end_date_required') ?? error_for('end_before_start')}
            </p>
          )}
        </div>
      </div>

      {/* Notes — optional */}
      <div>
        <label className="nv-label" htmlFor="my-leave-notes">
          {t('my_leave.form.notes_label')}
        </label>
        <Textarea
          id="my-leave-notes"
          value={notes}
          onChange={(e) => {
            set_notes(e.target.value);
          }}
          placeholder={t('my_leave.form.notes_placeholder')}
          aria-label={t('my_leave.form.notes_label')}
          disabled={is_submitting}
          maxLength={2000}
        />
      </div>

      {/* Server-side error */}
      {server_error && (
        <p className="nv-help is-error" role="alert">
          {server_error}
        </p>
      )}

      <div>
        <Button
          variant="primary"
          onClick={handle_submit}
          loading={is_submitting}
          disabled={is_submitting}
        >
          {is_submitting
            ? t('my_leave.form.submitting')
            : t('my_leave.form.submit_cta')}
        </Button>
      </div>
    </div>
  );
}
